import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import { StaticImage } from "gatsby-plugin-image";
import { navigate } from "gatsby";
import { LOAN_PURPOSE_MAP, specialLinksLoans } from "../constants";
import Spinner from "./Spinner";
import { Form, FormGroup, Input, Label, Button, Alert } from "reactstrap";
import { calculateMonthlyPayment, formatCurrency } from "../utils";
import "rc-slider/assets/index.css";
import Slider from "rc-slider";
import Select from "react-select";

const options = [
  { value: "Bil", label: "Bil" },
  { value: "Båt", label: "Båt" },
  { value: "MC", label: "MC" },
  { value: "Bodelning", label: "Bodelning" },
  { value: "Bostad - inredning", label: "Bostad - inredning" },
  { value: "Bostad - renovering", label: "Bostad - renovering" },
  { value: "Bouppteckning", label: "Bouppteckning" },
  { value: "Bröllop", label: "Bröllop" },
  { value: "Inkassoskuld", label: "Inkassoskuld" },
  { value: "Inv. i eget företag", label: "Inv. i eget företag" },
  { value: "Investering", label: "Investering" },
  { value: "Konsumtion", label: "Konsumtion" },
  { value: "Körkort", label: "Körkort" },
  { value: "Resor/nöjen", label: "Resor/nöjen" },
  { value: "Samla lån", label: "Samla lån" },
  { value: "Sjukvård/Operation", label: "Sjukvård/Operation" },
  { value: "Studier", label: "Studier" },
];

const InputDiv = styled.div`
  position: relative;
  display: inline-block;
  font-size: 1.5rem;
  width: 100%;
  input {
    padding-left: 10px;
    border: none;
    font-size: 2rem;
    border-bottom: 1px solid rgb(248, 152, 56);
  }

  span {
    position: absolute;
    left: 140px;
    top: 50%;
    transform: translateY(-50%);
    color: black;
    pointer-events: none;
  }
`;

const PartnerDiv = styled.div`
  background-color: white;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  border-radius: 5px;
  @media (max-width: 440px) {
    flex-direction: column;
  }
`;

const FormDiv = styled.div`
  color: #333;
  form {
    //background: #ffc107;
    padding: 1rem;
    border-radius: 5px;
    box-shadow: 0 2px 12px 0 #00000026, 0 1px 2px 0 #00000040;
  }
`;

type ApplicationType = {
  loanPurpose: string | null;
  repaymentTime: number;
  idNumber: string | null;
  ip_address: string | null;
  accommodationMonthlyCost: number | null;
  monthlyIncome: number | null;
  yearlyIncome: number | null;
  accommodationType: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  employmentStatus: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  civilStatus: string | null;
  loanAmount: string | number;
  formattedLoanAmount: string;
  allowCommunication: boolean | false;
  phoneNumber: string | null;
};

const extractAdTracking = (url) => {
  const params = new URL(url).searchParams;
  const gclid = params.get("gclid");
  const utmCampaign = params.get("utm_campaign");
  const utmSource = params.get("utm_source");
  const utmId = params.get("utm_id");
  const utmTerm = params.get("utm_term");

  return { gclid, utmCampaign, utmSource, utmId, utmTerm };
};

const LoanApplicationForm = (props) => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [ssnInvalid, setSsnInvalid] = useState(false);
  const [ssnInvalidMessage, setInvalidMessageSSN] = useState("");
  const [phoneInvalid, setPhoneInvalid] = useState(false);

  const [loanCalculationData, setLoanCalculationData] = useState({
    totalCost: 0,
    monthlyPayment: 0,
    loanCost: 0,
    effectiveRate: 0,
    loanAmount: 0,
    tenure: 0,
  });

  const [appData, setAppData] = React.useState<ApplicationType>({
    loanAmount: 285000,
    formattedLoanAmount: "285 000",
    email: "",
    repaymentTime: 12,
    ip_address: null,
    loanPurpose: null,
    idNumber: null,
    accommodationMonthlyCost: null,
    accommodationType: null,
    firstName: null,
    lastName: null,
    employmentStatus: null,
    monthlyIncome: null,
    yearlyIncome: null,
    address: null,
    postalCode: null,
    city: null,
    civilStatus: null,
    allowCommunication: false,
    phoneNumber: null,
  });

  async function sendApplication(): Promise<boolean> {
    sessionStorage.removeItem("axo.token");
    sessionStorage.removeItem("axo.application-id");

    const urlList = ["test.smartywak.com", "localhost", "127.0.0.1"];
    const isTestUrl = urlList.some((url) =>
      new RegExp(url).test(window.location.href)
    );

    const MAPPING = {
      appTerms: true,
      loanAmount: appData.loanAmount,
      ip_address: appData.ip_address,
      loanPurpose: "",
      repaymentYears: appData.repaymentTime,
      market: "SE",
      ssn: appData.idNumber,
      phone_number: appData.phoneNumber,
      email: appData.email,
      digital_communication: appData.allowCommunication,
      user_agreement: true,
      first_name: appData.firstName,
      last_name: appData.lastName,
      address: appData.address,
      postal_code: appData.postalCode,
      city: appData.city,
      civil_status: appData.civilStatus,
      test: isTestUrl,
      // TODO: Send ad tracking information to the backend
    };

    if (appData.loanPurpose) {
      MAPPING["loanPurpose"] = LOAN_PURPOSE_MAP[appData?.loanPurpose];
    }
    if (MAPPING.email.trim() == "") {
      return false;
    }
    const ip_address = localStorage.getItem("ip_address");
    MAPPING.ip_address = ip_address;
    try {
      const response = await fetch(
        `${process.env.GATSBY_API_URL}applications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(MAPPING),
        }
      );

      if (!response.ok) {
        const message = `Failed to send application: ${response?.status} ${response?.statusText}`;
        console.error(message);
        throw new Error(message);
      }

      const data = await response.json();
      // store id
      if (data.jwt && data.applicationID) {
        sessionStorage.setItem("axo.token", data.jwt);
        sessionStorage.setItem("axo.application-id", data.applicationID);
        return true;
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  }
  const handleLoanAmount = (
    evt: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>
  ) => {
    let number: string | number = evt.target.value;

    number = Number(number.replace(/\s+/g, ""));

    if (evt?.type != "change") {
      if (number < 10000) {
        number = 10000;
      }

      if (number > 800000) {
        number = 800000;
      }
    }

    let formattedNumber = new Intl.NumberFormat("sv", {
      style: "decimal",
    })
      .format(number)
      .replace(/,/g, " ");
    setAppData({
      ...appData,
      loanAmount: number,
      formattedLoanAmount: formattedNumber,
    });
  };

  const goToLoanPage = async (e) => {
    setShowSpinner(true);
    e.preventDefault();
    try {
      const sent = await sendApplication();
      setTimeout(() => {
        setShowSpinner(false);
        if (sent) {
          navigate("/lan");
        }
      }, 2000);
    } catch (err) {
      setShowSpinner(false);
    }
  };

  React.useEffect(() => {
    const { pathname, href } = window.location;
    try {
      // later - analyze the variance to support retargeting the ads
      const utmTracking = JSON.parse(
        window.localStorage.getItem("utmTracking") || "{}"
      );
      localStorage.setItem(
        "utmTracking",
        JSON.stringify({
          ...utmTracking,
          ...extractAdTracking(href),
        })
      );
    } catch (err) {
      localStorage.removeItem("utmTracking");
      console.error(err);
    }

    let loanAmount: undefined | number | null = specialLinksLoans[pathname];
    if (loanAmount) {
      setAppData({
        ...appData,
        loanAmount: Number(loanAmount),
        formattedLoanAmount: new Intl.NumberFormat("sv", {
          style: "decimal",
        })
          .format(loanAmount)
          .replace(/,/g, " "),
      });
    }
  }, []);

  const [steps, setStep] = useState({ level: 1 });

  React.useEffect(() => {
    const result = calculateMonthlyPayment(
      parseInt(appData.loanAmount),
      parseInt(appData.repaymentTime)
    );
    setLoanCalculationData(result);
  }, [appData.loanAmount, appData.repaymentTime]);

  return (
    <FormDiv style={{ ...props.style }}>
      <Form onSubmit={goToLoanPage}>
        <div>
          {steps?.level === 1 && (
            <>
              <FormGroup>
                <Label for="loanAmount">
                  <span style={{ fontSize: "2rem" }}>Lånebelopp</span>
                </Label>
                <div className="flex items-center">
                  <InputDiv>
                    <input
                      id="loanAmount"
                      type="text"
                      value={appData?.formattedLoanAmount}
                      style={{ width: "100%", borderRadius: "5px" }}
                      required
                      onBlur={handleLoanAmount}
                      onChange={handleLoanAmount}
                    />
                    <span style={{ marginLeft: "5px" }}>Kr</span>
                  </InputDiv>
                </div>
                <div className="mt-2" />
                <Slider
                  min={10000}
                  max={800000}
                  step={5000}
                  value={appData.loanAmount}
                  onChange={(value) =>
                    setAppData({
                      ...appData,
                      loanAmount: value,
                      formattedLoanAmount: new Intl.NumberFormat("sv", {
                        style: "decimal",
                      })
                        .format(value)
                        .replace(/,/g, " "),
                    })
                  }
                  trackStyle={{ backgroundColor: "#ffc107ff", height: 10 }}
                  handleStyle={{
                    borderColor: "#ffc107ff",
                    height: 30,
                    width: 30,
                    marginTop: -10,
                    backgroundColor: "#ffc107ff",
                  }}
                  railStyle={{ backgroundColor: "#6c757d", height: 10 }}
                />
              </FormGroup>

              <div>
                <Label for="loanAmount">
                  <span style={{ fontSize: "1.5rem" }}>
                    Välj lånetid: {appData.repaymentTime} år
                  </span>
                </Label>
                <Slider
                  max={20}
                  min={1}
                  step={1}
                  value={appData.repaymentTime}
                  trackStyle={{ backgroundColor: "#ffc107ff", height: 10 }}
                  handleStyle={{
                    borderColor: "#ffc107ff",
                    height: 30,
                    width: 30,
                    marginTop: -10,
                    backgroundColor: "#ffc107ff",
                  }}
                  railStyle={{ backgroundColor: "#6c757d", height: 10 }}
                  onChange={(value) =>
                    setAppData({
                      ...appData,
                      repaymentTime: value,
                    })
                  }
                />
              </div>
              <div className="mt-3" />
              <div className="mb-4">
                <strong>
                  Månadskostnad{" "}
                  {formatCurrency(
                    Math.floor(loanCalculationData.monthlyPayment || 0)
                  )}{" "}
                  kr*
                </strong>
              </div>
              <FormGroup className="position-relative">
                <Label for="email">E-post</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="Din e-post"
                  onBlur={(e) => {
                    const email = e.target.value.trim();
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    setEmailInvalid(!emailRegex.test(email));
                  }}
                  onChange={(e) =>
                    setAppData({
                      ...appData,
                      email: e.target.value.trim(),
                    })
                  }
                  invalid={emailInvalid}
                />
                {emailInvalid && (
                  <>
                    <div
                      style={{ fontSize: "0.75rem" }}
                      className="alert alert-danger show mt-1 "
                      role="alert"
                    >
                      {!appData.email.includes("@") ? (
                        <>En giltig e-postadress måste innehålla @</>
                      ) : (
                        <>Ogiltig e-postadress</>
                      )}
                    </div>
                  </>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="telephone">Telefonnummer</Label>
                <Input
                  id="telephone"
                  type="tel"
                  placeholder="077-- -- -- --"
                  required
                  onBlur={(e) => {
                    const phoneNumber = e.target.value
                      .trim()
                      .replace(/\s+/g, "")
                      .replaceAll("-", "");
                    const phoneRegex = /^(\+46|0)[7-9]\d{8}$/;
                    setPhoneInvalid(!phoneRegex.test(phoneNumber));
                  }}
                  onChange={(e) =>
                    setAppData({
                      ...appData,
                      phoneNumber: e.target.value.trim(),
                    })
                  }
                  invalid={phoneInvalid}
                />
                {phoneInvalid && (
                  <>
                    <div
                      style={{ fontSize: "0.75rem" }}
                      className="alert alert-danger alert-dismissible fade show mt-1 "
                      role="alert"
                    >
                      Mobilnumret verkar vara inkorrekt. Vänligen kontrollera så
                      det är ett svenskt mobilnummer
                    </div>
                  </>
                )}
              </FormGroup>

              <FormGroup switch className="mb-4 mt-2">
                <Input
                  type="switch"
                  role="switch"
                  style={{ cursor: "pointer" }}
                  id="allowCommunication"
                  defaultChecked={appData.allowCommunication}
                  onClick={() =>
                    setAppData({
                      ...appData,
                      allowCommunication: !appData.allowCommunication,
                    })
                  }
                />
                <Label
                  check
                  htmlFor="allowCommunication"
                  style={{ fontSize: "0.75rem" }}
                >
                  Jag samtycker till att Smartywak använder mina personuppgifter
                  för att via e-post, textmeddelande eller post skicka
                  smartywak.com nyhetsbrev om allmänna villkor på lånemarknaden,
                  privatekonomi och uppdaterade sammanställningar över lån med{" "}
                  <a target="_blank" href="/integritetspolicy/">
                    Läs mer här.
                  </a>
                </Label>
              </FormGroup>
              <Button
                style={{ width: "100%" }}
                disabled={appData.phoneNumber == null && appData.email == ""}
                data-umami-event="Go to step 2"
                onClick={() => {
                  setStep({ ...steps, level: 2 });
                  localStorage.setItem(
                    "savedApplication",
                    JSON.stringify(appData)
                  );
                }}
              >
                Påbörja ansökan &rarr;
              </Button>
            </>
          )}
          {steps.level === 2 && (
            <>
              <FormGroup>
                <Label htmlFor="firstName">Förnamn</Label>
                <Input
                  id="firstName"
                  type="text"
                  required
                  onChange={(e) =>
                    setAppData({
                      ...appData,
                      firstName: e.target.value.trim(),
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Efternamn</Label>
                <Input
                  id="lastName"
                  type="text"
                  required
                  onChange={(e) =>
                    setAppData({
                      ...appData,
                      lastName: e.target.value.trim(),
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="idNumber">Ditt personnummer</Label>
                <Input
                  id="idNumber"
                  type="text"
                  required
                  invalid={ssnInvalid}
                  onBlur={async (e) => {
                    try {
                      const response = await fetch(
                        `${process.env.GATSBY_API_URL}validate-ssn`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            ssn: e.target.value.trim(),
                            market: "se",
                          }),
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Failed to validate SSN");
                      }

                      const data = await response.json();
                      if (data?.age < 18) {
                        setSsnInvalid(true);
                        setInvalidMessageSSN("Du måste vara äldre än 18.");
                      } else if (data?.age > 65) {
                        setSsnInvalid(true);
                        setInvalidMessageSSN(
                          "Du måste vara mellan 18 och 65 år."
                        );
                      } else {
                        setSsnInvalid(false);
                        setAppData({
                          ...appData,
                          idNumber: data.ssn,
                        });
                      }
                    } catch (err) {
                      setSsnInvalid(true);
                    }
                  }}
                  onChange={(e) =>
                    setAppData({
                      ...appData,
                      idNumber: e.target.value.trim(),
                    })
                  }
                  placeholder="ÅÅÅÅMMDDXXXX"
                />

                {ssnInvalid && ssnInvalidMessage == "" && (
                  <>
                    <div
                      style={{ fontSize: "0.75rem" }}
                      className="alert alert-danger show mt-1 "
                      role="alert"
                    >
                      Ditt personnummer är ogiltigt.
                    </div>
                  </>
                )}
                {ssnInvalid && ssnInvalidMessage != "" && (
                  <>
                    <div
                      style={{ fontSize: "0.75rem" }}
                      className="alert alert-danger show mt-1 "
                      role="alert"
                    >
                      {ssnInvalidMessage}
                    </div>
                  </>
                )}
              </FormGroup>
              <FormGroup>
                <label htmlFor="reason">Syfte för lån</label>
                <br />

                <Select
                  options={options}
                  required
                  name="reason"
                  id="reason"
                  onChange={(loanPurposeObject) => {
                    setAppData({
                      ...appData,
                      loanPurpose: loanPurposeObject?.value || "",
                    });
                  }}
                />
              </FormGroup>
              <br />
              <Button
                disabled={
                  !appData.firstName ||
                  !appData.lastName ||
                  !appData.idNumber ||
                  !appData.loanPurpose ||
                  ssnInvalid ||
                  emailInvalid ||
                  phoneInvalid
                }
                type="submit"
                data-umami-event="send-application"
              >
                {showSpinner && <Spinner />}
                {!showSpinner && <span>GÅ VIDARE</span>}
              </Button>
            </>
          )}
        </div>

        <div className="mt-4"></div>
        {/* A/B test alert */}
        {steps.level !== 2 && (
          <Alert color="warning" fade={false}>
            <p style={{ fontSize: "0.75rem" }}>
              ✅ När du ansöker får du även tillgång till din kreditscore helt
              kostnadsfritt på uScore.se
              <br />✅ Långivarnas ränta är rörlig och sätts{" "}
              <strong>individuellt</strong>
              <br />
              ✅ Lånetid mellan 1 - 20 år
              <br />✅ Nominell ränta mellan <strong>3.95%</strong> -{" "}
              <strong>29.00%</strong>
              <br />✅ Effektiv ränta mellan <strong>3.99%</strong> -{" "}
              <strong>33.32%</strong>
              <br />✅ Startavgift mellan <strong>0 kr - 895 kr</strong>
              <br />✅ <strong>Låneexempel:</strong> Annuitetslån{" "}
              {formatCurrency(loanCalculationData.loanAmount)} kr på{" "}
              {loanCalculationData.tenure / 12} år (180 avbetalningar).
              Nom.ränta: 5.91%. Eff. ränta: 6.09% per 2021-01-29. Totalt att
              betala{" "}
              {formatCurrency(Math.floor(loanCalculationData.totalCost || 0))}{" "}
              kr. Månadskostnad:{" "}
              {formatCurrency(
                Math.floor(loanCalculationData.monthlyPayment || 0)
              )}{" "}
              kr.
            </p>
          </Alert>
        )}

        {!props?.skipPartner && (
          <PartnerDiv>
            <span>Vi är i samarbete med:</span>{" "}
            <StaticImage
              placeholder="blurred"
              src="../images/axofinans.png"
              alt="Axo Finans"
            />
          </PartnerDiv>
        )}
      </Form>
    </FormDiv>
  );
};

export default LoanApplicationForm;
